import React, { useState, useEffect, useCallback } from 'react';
import {
  getGiftCertificatesConfig, getGiftCertificates, issueGiftCertificate, retryGiftCertificate,
  sendGiftCertificate, refreshGiftCertificates, giftCertificatePdfUrl,
} from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatMoney, formatDate, formatDateTime } from '../../utils/formatters';

/* Иконки рисуем штрихом вместо эмодзи (см. Navbar.jsx). */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconWarning = () => <Icon><path d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z" /><path d="M12 8V13" /><path d="M12 16H12.0001" /></Icon>;
const IconRefresh = () => <Icon><path d="M20.4 10.6C19.6 6.8 16.1 4 12 4C7.6 4 4 7.6 4 12C4 16.4 7.6 20 12 20C15.3 20 18.1 18 19.3 15.2" /><path d="M20 4V10.6H13.4" /></Icon>;
const IconCheckCircle = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M8.5 12.5L10.5 14.5L15.5 9.5" /></Icon>;

/* Наш статус выпуска + состояние в iiko → одна понятная плашка. */
const certBadge = (c) => {
  if (c.status === 'failed') return { cls: 'st-lost', text: 'Не выпущен' };
  if (c.status === 'issuing') return { cls: 'st-risk', text: 'Выпускается…' };
  if (c.status !== 'issued') return { cls: 'st-unknown', text: 'Ждёт оплаты' };
  if (c.iiko_status === 'Заблокирован') return { cls: 'st-lost', text: 'Заблокирован' };
  if (c.iiko_status === 'Нет в iiko') return { cls: 'st-lost', text: 'Нет в iiko' };
  if (c.iiko_balance === 0) return { cls: 'st-unknown', text: 'Израсходован' };
  if (c.valid_to && c.valid_to < new Date().toISOString().slice(0, 10)) return { cls: 'st-lost', text: 'Истёк' };
  return { cls: 'st-active', text: 'Действует' };
};

const errorText = (err, fallback) => err.response?.data?.error
  || err.response?.data?.certificate?.last_error || fallback;

const IssueForm = ({ config, onIssued }) => {
  const [nominal, setNominal] = useState(String(config.nominals[0] || 1000));
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const amount = Number(nominal);
  const amountOk = amount >= config.min_nominal && amount <= config.max_nominal;

  const submit = async (e) => {
    e.preventDefault();
    if (!amountOk) return;
    const ok = window.confirm(
      `Будет выпущен и сразу активирован настоящий сертификат iiko на ${formatMoney(amount)}. ` +
      'По нему можно будет расплатиться на кассе. Продолжить?'
    );
    if (!ok) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await issueGiftCertificate({
        nominal: amount, buyer_name: buyerName, buyer_email: buyerEmail, buyer_phone: buyerPhone,
        recipient_name: recipient, message, send_email: sendEmail,
      });
      setResult(res.certificate);
      setBuyerName(''); setBuyerEmail(''); setBuyerPhone(''); setRecipient(''); setMessage('');
    } catch (err) {
      setError(errorText(err, 'Не удалось выпустить сертификат'));
    } finally {
      setBusy(false);
      onIssued();
    }
  };

  return (
    <form className="panel crm-promo-form" onSubmit={submit}>
      <h4>Выпустить сертификат</h4>
      <div className="crm-cert-nominals">
        {config.nominals.map((n) => (
          <button
            type="button" key={n}
            className={`crm-tab ${Number(nominal) === n ? 'active' : ''}`}
            onClick={() => setNominal(String(n))}
          >
            {formatMoney(n)}
          </button>
        ))}
        <input
          type="number" min={config.min_nominal} max={config.max_nominal} step="100"
          className="crm-cert-custom" aria-label="Свой номинал, ₽" placeholder="Свой номинал, ₽"
          value={nominal} onChange={(e) => setNominal(e.target.value)}
        />
      </div>
      <div className="crm-promo-form-row">
        <input type="text" placeholder="Покупатель" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
        <input type="email" placeholder="Почта покупателя" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} />
        <input type="tel" placeholder="Телефон" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} />
      </div>
      <div className="crm-promo-form-row">
        <input type="text" placeholder="Кому (будет на сертификате)" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
        <input type="text" maxLength={300} placeholder="Поздравление (будет на сертификате)" value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <label className="crm-cert-check">
        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
               disabled={!config.mail_configured} />
        Отправить PDF на почту покупателя
        {!config.mail_configured && ' (почта на сервере не настроена)'}
      </label>
      {error && <div className="crm-promo-error"><IconWarning /> {error}</div>}
      {result && (
        <div className="crm-cert-result">
          <IconCheckCircle />
          <div>
            Сертификат <strong>№ {result.number}</strong> на {formatMoney(result.nominal)} выпущен
            и активирован в iiko{result.valid_to ? `, действует до ${formatDate(result.valid_to)}` : ''}.
            {result.sent_to && <> Отправлен на {result.sent_to}.</>}
            {result.last_error && <div className="crm-cert-warn">{result.last_error}</div>}
          </div>
          <a className="ui-btn-ghost" href={giftCertificatePdfUrl(result.id)} target="_blank" rel="noreferrer">PDF</a>
        </div>
      )}
      <button className="btn-primary" type="submit" disabled={busy || !amountOk}>
        {busy ? 'Выпускаю в iiko…' : `Выпустить на ${amountOk ? formatMoney(amount) : '…'}`}
      </button>
      <div className="crm-cert-hint">
        Многоразовый, срок {config.lifetime_days} дней. Номер подбирается автоматически и не совпадает
        с уже выпущенными в iiko.
      </div>
    </form>
  );
};

const CertificateRow = ({ cert, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const badge = certBadge(cert);

  const retry = async () => {
    setBusy(true);
    try {
      await retryGiftCertificate(cert.id);
    } catch (err) {
      window.alert(errorText(err, 'Не удалось выпустить'));
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  const send = async () => {
    const email = window.prompt('На какую почту отправить сертификат?', cert.buyer_email || '');
    if (!email) return;
    setBusy(true);
    try {
      await sendGiftCertificate(cert.id, email.trim());
    } catch (err) {
      window.alert(errorText(err, 'Не удалось отправить письмо'));
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  return (
    <tr>
      <td>
        <div className="gb-name crm-cert-number">{cert.number}</div>
        <div className="gb-owner">{cert.source === 'online' ? 'Сайт' : 'Панель'} · {formatDateTime(cert.created_at)}</div>
      </td>
      <td className="gb-money">{formatMoney(cert.nominal)}</td>
      <td className="gb-money">{cert.iiko_balance != null ? formatMoney(cert.iiko_balance) : '—'}</td>
      <td>
        <div>{cert.buyer_name || '—'}</div>
        <div className="gb-owner">{[cert.buyer_phone, cert.buyer_email].filter(Boolean).join(' · ')}</div>
        {cert.recipient_name && <div className="gb-owner">Кому: {cert.recipient_name}</div>}
      </td>
      <td>
        <span className={`gb-badge ${badge.cls}`}>{badge.text}</span>
        {cert.valid_to && <div className="gb-owner">до {formatDate(cert.valid_to)}</div>}
        {cert.last_error && <div className="crm-cert-warn" title={cert.last_error}>{cert.last_error}</div>}
      </td>
      <td>{cert.sent_at ? <>{formatDateTime(cert.sent_at)}<div className="gb-owner">{cert.sent_to}</div></> : '—'}</td>
      <td className="crm-cert-actions">
        {cert.status === 'issued' && (
          <>
            <a className="ui-btn-ghost" href={giftCertificatePdfUrl(cert.id)} target="_blank" rel="noreferrer">PDF</a>
            <button className="ui-btn-ghost" onClick={send} disabled={busy}>Отправить</button>
          </>
        )}
        {cert.status === 'failed' && (
          <button className="ui-btn-ghost" onClick={retry} disabled={busy}>{busy ? 'Выпускаю…' : 'Повторить'}</button>
        )}
      </td>
    </tr>
  );
};

const CertificatesTab = () => {
  const [config, setConfig] = useState(null);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState('');

  useEffect(() => {
    getGiftCertificatesConfig().then(setConfig).catch(() => setConfig({ enabled: false, nominals: [] }));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getGiftCertificates(search ? { search } : {});
      setCerts(res.certificates || []);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const refresh = async () => {
    setRefreshing(true);
    setRefreshNote('');
    try {
      const res = await refreshGiftCertificates();
      setRefreshNote(`Проверено в iiko: ${res.checked}`);
      await load();
    } catch (err) {
      setRefreshNote(errorText(err, 'iiko.biz не ответил'));
    } finally {
      setRefreshing(false);
    }
  };

  if (!config) return <LoadingSpinner />;

  return (
    <>
      {config.enabled ? (
        <IssueForm config={config} onIssued={load} />
      ) : (
        <div className="panel crm-promo-form">
          <div className="crm-promo-error">
            <IconWarning /> Выпуск сертификатов работает через iiko.biz, а доступ к нему настроен не для этой организации.
          </div>
        </div>
      )}

      <div className="crm-filters">
        <input
          type="search" className="gb-search" placeholder="Номер, покупатель, почта, телефон"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        {config.enabled && (
          <button className="ui-btn-ghost crm-export-btn" onClick={refresh} disabled={refreshing}>
            <IconRefresh /> {refreshing ? 'Сверяю с iiko…' : 'Остатки из iiko'}
          </button>
        )}
        {refreshNote && <span className="gb-owner">{refreshNote}</span>}
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="panel gb-table-panel">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Номер</th><th>Номинал</th><th>Остаток</th><th>Покупатель</th>
                  <th>Статус</th><th>Письмо</th><th></th>
                </tr>
              </thead>
              <tbody>
                {certs.map((c) => <CertificateRow key={c.id} cert={c} onChanged={load} />)}
                {certs.length === 0 && (
                  <tr><td colSpan={7} className="crm-empty">
                    {search ? 'Ничего не нашлось' : 'Пока не выпущено ни одного сертификата'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default CertificatesTab;
